import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormControl, Validators, AbstractControl, FormGroup, FormArray } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AgrimotorService } from '../agrimotor.service';
import { formatDate } from '@angular/common';
import { ActivatedRoute } from "@angular/router";
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, of } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-ag-drivers',
  templateUrl: './drivers.component.html',
  styleUrls: ['./drivers.component.css', '../../quote.component.css']
})
export class AgDriversComponent implements OnInit {
  @Input() staticData;
  @Input() authDetails;
  @Input() selectedIndex;
  frmFields: any;
  submitted = false;
  @Input() modelObj;
  @Input() apiModelObj;
  @Output() modelObjUpdated = new EventEmitter();

  constructor(private fb: FormBuilder, private modalService: NgbModal, private as: AgrimotorService, private cdr: ChangeDetectorRef,
    private route: ActivatedRoute, private _snackBar: MatSnackBar) { }

  maxDOB: any = '';

  // Custom validator that allows boolean false but rejects null/undefined/empty
  private requiredSelection(control: AbstractControl): { [key: string]: boolean } | null {
    return (control.value !== null && control.value !== undefined && control.value !== '') ? null : { required: true };
  }

  ngOnInit(): void {
    this.frmFields = this.fb.group({
      driver_conviction_declaration: ['', this.requiredSelection],
      driver_claim_declaration: ['', this.requiredSelection],
      drivers: this.fb.array([]),
    });
    let current_date = new Date();
    current_date.setFullYear(new Date().getFullYear() - 16);
    this.maxDOB = current_date.toISOString().slice(0, 10);
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  parentModelObj;
  ngOnChanges(changes: SimpleChanges) {
    this.parentModelObj = this.modelObj;
    if (this.selectedIndex == 2 || this.selectedIndex == 3) {
      if (this.modelObj.drivers && this.modelObj.drivers.length > 0) {
        this.drivers.clear();
        this.modelObj.drivers.forEach((res) => {
          // Build through initDrivers() so the rebuilt rows keep their validators.
          // Constructing bare controls here is what let a driver be saved with no
          // title / DOB / licence details at all (NB-27YKH).
          const driver = this.initDrivers();
          driver.patchValue({
            id: res.id,
            title: res.title,
            first_name: res.first_name,
            last_name: res.last_name,
            dob: res.dob,
            additional_trades: res.additional_trades || [],
            licence_type: res.licence_type,
            licence_length: res.licence_length,
          });
          this.drivers.push(driver);
        })
      }
    }

    const application = this.as.applicationOf(this.apiModelObj);
    if (this.apiModelObj && application) {
      const meta = this.as.quoteMetaOf(this.apiModelObj);
      this.modelObj = application;
      this.modelObj.id = meta.id;
      this.modelObj.reference = meta.reference;
      this.modelObj.status = meta.status;
      this.modelObj.sequence_number = this.apiModelObj.sequence_number;
      this.patchValues();
    }
  }

  get f(): { [key: string]: AbstractControl } {
    return this.frmFields.controls;
  }

  get drivers(): FormArray {
    return this.frmFields.get("drivers") as FormArray
  }

  initDrivers() {
    return this.fb.group({
      id: [''],
      title: ['', Validators.required],
      first_name: ['', Validators.required],
      last_name: [''],
      dob: ['', Validators.required],
      additional_trades: [[]],
      licence_type: ['', Validators.required],
      licence_length: ['', Validators.required],
      claims: [''],
      convictions: [''],
    })
  }

  patchValues() {
    this.frmFields.patchValue({
      driver_conviction_declaration: this.modelObj.driver_conviction_declaration,
      driver_claim_declaration: this.modelObj.driver_claim_declaration,
      drivers: this.modelObj.drivers,
    });
  }

  // "Insured only" / "Insured and Spouse" never require named drivers; every other
  // driving restriction that mentions named drivers does (Insured and Named, Named
  // Drivers, Named Drivers excluding Policyholder, Any driver aged over N other than
  // those/other named, ...). Matching on the substring rather than the exact sentence
  // avoids depending on backend wording we can't confirm (e.g. "other than other named"
  // vs "other than those named").
  private noNamedDriverRestrictions = ['insured only', 'insured and spouse'];

  requiresNamedDrivers(): boolean {
    const vehicles = this.modelObj?.vehicles || [];
    return vehicles.some(v => {
      const restriction = (v.driving_restriction || '').toString().trim().toLowerCase();
      if (!restriction || this.noNamedDriverRestrictions.includes(restriction)) { return false; }
      return restriction.includes('named');
    });
  }

  requiresBusinessUseDrivers(): boolean {
    const vehicles = this.modelObj?.vehicles || [];
    return vehicles.some(v => v.extended_use?.driver_business_use === true);
  }

  driversRequired(): boolean {
    return !!(this.f.driver_conviction_declaration.value || this.f.driver_claim_declaration.value
      || this.requiresNamedDrivers() || this.requiresBusinessUseDrivers());
  }

  // Always live — never goes stale relative to modelObj/vehicle changes,
  // unlike a flag that only got recomputed from two specific (change) events.
  get showDrivers(): boolean {
    return this.driversRequired() || !!(this.modelObj?.drivers && this.modelObj.drivers.length > 0);
  }

  addNewDriver() {
    this.isDriverNodeEmpty = false;
    this.drivers.push(this.initDrivers());
  }

  removeDriver(index: number) {
    this.drivers.removeAt(index);
  }

  helptext = '';
  helpTextPopup(content, text) {
    this.modalService.open(content, { centered: true });
    this.helptext = text;
  }

  // The ag-claims / ag-convictions child controls emit either a bare array or the
  // `{ claims: [...] }` / `{ convictions: [...] }` wrapper their internal form group
  // produces, depending on whether the user has touched them yet.
  private unwrapList(value: any, key: 'claims' | 'convictions'): any[] {
    if (Array.isArray(value)) { return value; }
    if (value && Array.isArray(value[key])) { return value[key]; }
    return [];
  }

  private countAcrossDrivers(key: 'claims' | 'convictions'): number {
    return (this.drivers.controls || []).reduce((total, driver) => {
      const list = this.unwrapList(driver.get(key)?.value, key);
      const populated = key === 'claims'
        ? list.filter((c: any) => c && c.date && c.type)
        : list.filter((c: any) => c && c.code && c.date);
      return total + populated.length;
    }, 0);
  }

  /** A declared claim has to be recorded against a driver before the quote can move on. */
  get missingDriverClaims(): boolean {
    return this.f.driver_claim_declaration.value === true && this.countAcrossDrivers('claims') === 0;
  }

  /** Likewise for a declared conviction. */
  get missingDriverConvictions(): boolean {
    return this.f.driver_conviction_declaration.value === true && this.countAcrossDrivers('convictions') === 0;
  }

  /**
   * Gives every driver still lacking one its own id from the agri-api's generate-id
   * endpoint, then completes. Emits once even when nothing needs an id.
   */
  private assignMissingDriverIds() {
    const needsId = (this.modelObj.drivers || []).filter((d: any) => !d.id);
    if (needsId.length === 0) { return of(null); }

    const requests = needsId.map((driver: any) =>
      this.as.getUniqueID(this.authDetails).pipe(
        tap((response: any) => {
          if (response?.meta?.id) { driver.id = response.meta.id; }
        })
      )
    );
    return forkJoin(requests);
  }

  isDriverNodeEmpty: boolean = null;
  onSubmit() {
    this.submitted = true;
    if (!this.frmFields.valid) {
      this._snackBar.open('Please complete the highlighted mandatory driver details before proceeding.', '', {
        duration: 5000, panelClass: 'text-danger'
      });
      return false;
    }
    if (this.missingDriverClaims) {
      this._snackBar.open('A motor accident/claim has been declared — please add the claim to the relevant driver.', '', {
        duration: 5000, panelClass: 'text-danger'
      });
      return false;
    }
    if (this.missingDriverConvictions) {
      this._snackBar.open('A motoring conviction has been declared — please add the conviction to the relevant driver.', '', {
        duration: 5000, panelClass: 'text-danger'
      });
      return false;
    }
    let formData = this.frmFields.value;
    this.modelObj.driver_conviction_declaration = formData.driver_conviction_declaration;
    this.modelObj.driver_claim_declaration = formData.driver_claim_declaration;
    this.modelObj.drivers = formData.drivers;
    if (this.modelObj.drivers && this.modelObj.drivers.length > 0) {
      this.isDriverNodeEmpty = false;
      this.modelObj.drivers.forEach((res) => {
        res.dob = formatDate(res.dob, 'yyyy-MM-dd', 'en-US');
        res.licence_length = formatDate(res.licence_length, 'yyyy-MM-dd', 'en-US');
        if (res.claims && res.claims.claims) {
          res.claims = res.claims.claims;
        }
        if (res.claims && Array.isArray(res.claims)) {
          res.claims = res.claims.filter((c: any) => c.date && c.type);
          res.claims.forEach((c: any) => {
            if (c.date instanceof Date) {
              c.date = formatDate(c.date, 'yyyy-MM-dd', 'en-US');
            } else if (typeof c.date === 'string' && c.date.includes('T')) {
              c.date = c.date.split('T')[0];
            }
          });
        }
        if (res.convictions && res.convictions.convictions) {
          res.convictions = res.convictions.convictions;
        }
      })
    } else {
      if (this.driversRequired()) {
        this.isDriverNodeEmpty = true;
        return;
      }
    }

    // One generate-id call per driver that still needs an id. The previous code made a
    // single call and stamped the SAME id onto every unidentified driver, which meant
    // two new drivers became indistinguishable to the insurer.
    this.assignMissingDriverIds().subscribe(
        () => {
            if (this.modelObj.type == 2) {
              this.modelObj.policy_id = null;
              this.modelObj.effective_date = null;
            }

            if (this.modelObj.vehicles && this.modelObj.vehicles.length > 0) {
              // Drop the legacy typed arrays from modelObj itself (not just the outgoing
              // copy) — otherwise the merge-not-replace fix resurrects them on the next save.
              delete this.modelObj.agricultural_vehicles;
              delete this.modelObj.cars;
              delete this.modelObj.commercial_vehicles;
              delete this.modelObj.horseboxes;
              delete this.modelObj.trailers;
            }
            const attributes = { ...this.modelObj };
            let data = {
              data: {
                "type": this.modelObj.type,
                "attributes": attributes,
                "id": this.modelObj.id,
                "broker_id": this.modelObj.broker_id,
                "sequence_number": this.modelObj.sequence_number,
              }
            };
            this.as.saveQuote(data)
              .subscribe(
                (res: any) => {
                  if (res.success) {
                    const version = res.result?.quote_data?.quote_versions?.[0];
                    const savedApplication = this.as.applicationOf(version);
                    if (version && savedApplication) {
                      const meta = this.as.quoteMetaOf(version);
                      // Merge (not replace) — a save response that omits a field must not
                      // erase data already entered in another tab (e.g. proposer/fleet experience).
                      this.modelObj = { ...this.modelObj, ...savedApplication };
                      this.modelObj.id = meta.id;
                      this.modelObj.broker_id = version.broker_id;
                      this.modelObj.reference = meta.reference;
                      this.modelObj.status = meta.status;
                      this.modelObj.company_id = version.company_id;
                      this.modelObj.sequence_number = version.sequence_number;
                      this.modelObj.is_send_email = version.is_send_email;
                      if (version.financial_details) {
                        this.modelObj.broker_commission = version.financial_details.broker_commission;
                        this.modelObj.broker_admin_fee = version.financial_details.broker_admin_fee;
                      }
                      if (version.policy_holder) {
                        this.modelObj.proposer.email = version.policy_holder.email;
                        this.modelObj.proposer.phone = version.policy_holder.phone;
                        this.modelObj.proposer.is_email = version.policy_holder.is_email;
                        this.modelObj.proposer.is_phone = version.policy_holder.is_phone;
                        this.modelObj.proposer.sms = version.policy_holder.sms;
                        this.modelObj.proposer.post = version.policy_holder.post;
                      }
                      if (meta.premium) {
                        this.modelObj.is_referred = meta.premium.referred;
                      }

                      this.modelObjUpdated.emit(this.modelObj);
                    }
                  } else {
                    this._snackBar.open(res.message, '', {
                      duration: 5000,
                      panelClass: 'text-danger'
                    });
                  }
                },
                () => {
                  this._snackBar.open('Error saving drivers. Please try again.', '', {
                    duration: 5000, panelClass: 'text-danger'
                  });
                });
        },
        () => {
          this._snackBar.open('Could not allocate driver references. Please try again.', '', {
            duration: 5000, panelClass: 'text-danger'
          });
        });
  }
}
